import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  Search, Edit2, Trash2, Shield, ArrowLeft, Save,
  ChevronDown, ChevronRight, Users, AlertCircle, Loader2,
  ShieldCheck, Eye, Info, Copy, FileText,
  LayoutDashboard, Package, PieChart, Heart, QrCode, ShoppingCart, X,
  ChefHat, Smartphone, Plug, Sparkles,
} from "lucide-react";
import roleService from "../../../services/roleService";
import Pagination from "@/components/shared/Pagination";
import { useAuth } from "../../../context/AuthContext";
import { P } from "../../../config/permissionCodes";

// ─── SPECIAL PERMISSIONS ──────────────────────────────────────────────────────
// Permissions that render with an extra inline input (max value / cap / limit).
const MAX_DISCOUNT_PERM_CODE = "pos.dine_in.discount";

// ─── MODULE METADATA ──────────────────────────────────────────────────────────

const MODULE_META = {
  "Back Office": { icon: LayoutDashboard, iconBg: "bg-gradient-to-br from-indigo-500 to-indigo-600", headerBg: "bg-gradient-to-r from-indigo-50 to-indigo-100/50", badge: "bg-indigo-100 text-indigo-700", border: "border-indigo-200" },
  "POS":         { icon: ShoppingCart,    iconBg: "bg-gradient-to-br from-teal-500 to-teal-600",    headerBg: "bg-gradient-to-r from-teal-50 to-teal-100/50",    badge: "bg-teal-100 text-teal-700",    border: "border-teal-200" },
  "Inventory":   { icon: Package,         iconBg: "bg-gradient-to-br from-emerald-500 to-emerald-600", headerBg: "bg-gradient-to-r from-emerald-50 to-emerald-100/50", badge: "bg-emerald-100 text-emerald-700", border: "border-emerald-200" },
  "Reports":     { icon: PieChart,        iconBg: "bg-gradient-to-br from-amber-500 to-amber-600",   headerBg: "bg-gradient-to-r from-amber-50 to-amber-100/50",   badge: "bg-amber-100 text-amber-700",   border: "border-amber-200" },
  "CRM":         { icon: Heart,           iconBg: "bg-gradient-to-br from-rose-500 to-rose-600",    headerBg: "bg-gradient-to-r from-rose-50 to-rose-100/50",    badge: "bg-rose-100 text-rose-700",    border: "border-rose-200" },
  "HRM":         { icon: Users,           iconBg: "bg-gradient-to-br from-blue-500 to-blue-600",    headerBg: "bg-gradient-to-r from-blue-50 to-blue-100/50",    badge: "bg-blue-100 text-blue-700",    border: "border-blue-200" },
  "QR Menu":     { icon: QrCode,          iconBg: "bg-gradient-to-br from-purple-500 to-purple-600", headerBg: "bg-gradient-to-r from-purple-50 to-purple-100/50", badge: "bg-purple-100 text-purple-700", border: "border-purple-200" },
  "KDS":         { icon: ChefHat,        iconBg: "bg-gradient-to-br from-orange-500 to-orange-600", headerBg: "bg-gradient-to-r from-orange-50 to-orange-100/50", badge: "bg-orange-100 text-orange-700", border: "border-orange-200" },
  "Captain App": { icon: Smartphone,     iconBg: "bg-gradient-to-br from-sky-500 to-sky-600",      headerBg: "bg-gradient-to-r from-sky-50 to-sky-100/50",      badge: "bg-sky-100 text-sky-700",      border: "border-sky-200" },
  "Integrations":{ icon: Plug,           iconBg: "bg-gradient-to-br from-cyan-500 to-cyan-600",    headerBg: "bg-gradient-to-r from-cyan-50 to-cyan-100/50",    badge: "bg-cyan-100 text-cyan-700",    border: "border-cyan-200" },
  "AI Insights": { icon: Sparkles,       iconBg: "bg-gradient-to-br from-violet-500 to-purple-600",headerBg: "bg-gradient-to-r from-violet-50 to-violet-100/50",badge: "bg-violet-100 text-violet-700",border: "border-violet-200" },
};

const PARENT_ORDER = ["Back Office", "POS", "Inventory", "Reports", "CRM", "HRM", "QR Menu", "KDS", "Captain App", "Integrations", "AI Insights"];

// ─── ROOT ─────────────────────────────────────────────────────────────────────

export default function RoleView({ renderActions }) {

  const { hasPermission } = useAuth();

  const canViewRoles = hasPermission(P.HRM.ROLES.SCREEN);
  const canAddRoles = hasPermission(P.HRM.ROLES.ADD);
  const canEditRoles = hasPermission(P.HRM.ROLES.EDIT);
  const canDeleteRoles = hasPermission(P.HRM.ROLES.DELETE);
  const [screen, setScreen]           = useState("list");
  const [roles, setRoles]             = useState([]);
  const [loading, setLoading]         = useState(true);
  const [apiError, setApiError]       = useState(null);
  const [editingRole, setEditingRole] = useState(null);
  const [viewOnly, setViewOnly]       = useState(false);

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    setApiError(null);
    try {
      const data = await roleService.getAllRoles();
      setRoles(data);
    } catch {
      setApiError("Failed to load roles. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRoles(); }, [fetchRoles]);

  const handleAddNew = useCallback(() => {
    setEditingRole(null);
    setViewOnly(false);
    setScreen("form");
  }, []);

  useEffect(() => {
    if (!renderActions) return;

    if (canAddRoles) {
      renderActions({
        onRefresh: fetchRoles,
        onAddNew: handleAddNew
      });
    } else {
      renderActions({
        onRefresh: fetchRoles
      });
    }
  }, [fetchRoles, handleAddNew, renderActions, canAddRoles]);

  const handleEdit = (r) => { setEditingRole(r); setViewOnly(false); setScreen("form"); };
  const handleView = (r) => { setEditingRole(r); setViewOnly(true);  setScreen("form"); };

  const handleDelete = async (role) => {
    try {
      await roleService.deleteRole(role.id);
      setRoles((prev) => prev.filter((r) => r.id !== role.id));
    } catch {
      alert("Failed to delete role. Please try again.");
    }
  };

  // FIX 2: After save, go back to list and re-fetch roles for fresh data
  const handleSave = async (roleData, permissions) => {
    try {
      if (editingRole) {
        await roleService.updateRole(editingRole.id, {
          ...editingRole, ...roleData, permissions,
        });
      } else {
        await roleService.createRole({ ...roleData, permissions });
      }
      setScreen("list");
      await fetchRoles(); // Auto-reload the role list
    } catch {
      throw new Error("Save failed");
    }
  };

  if (!canViewRoles) {
    return (
      <div className="flex items-center justify-center h-full py-24">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-slate-900 mb-2">
            Access Denied
          </h3>
          <p className="text-sm text-slate-500">
            You don't have permission to view this page.
          </p>
        </div>
      </div>
    );
  }

  if (screen === "form") {
    return (
      <RoleFormPage
        role={editingRole}
        roles={roles}
        readOnly={viewOnly}
        onSave={handleSave}
        onCancel={() => setScreen("list")}
      />
    );
  }

  return (
    <div className="flex flex-col">
      <RoleList
        roles={roles}
        loading={loading}
        apiError={apiError}
        onRefresh={fetchRoles}
        onEdit={handleEdit}
        onView={handleView}
        onDelete={handleDelete}
        canEditRoles={canEditRoles}
        canDeleteRoles={canDeleteRoles}
      />
    </div>
  );
}

// ─── SCREEN 1: ROLE LIST ──────────────────────────────────────────────────────

function RoleList({
  roles,
  loading,
  apiError,
  onRefresh,
  onEdit,
  onView,
  onDelete,
  canEditRoles,
  canDeleteRoles
}) {
  const [search, setSearch]             = useState("");
  const [currentPage, setCurrentPage]   = useState(1);
  const [pageSize, setPageSize]         = useState(10);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [deleting, setDeleting]         = useState(false);

  const filtered = useMemo(
    () => roles.filter((r) =>
      [r.name || "", r.code || "", r.description || ""].some((v) =>
        v.toLowerCase().includes(search.toLowerCase())
      )
    ),
    [roles, search]
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated  = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await onDelete(deleteDialog);
    } finally {
      setDeleting(false);
      setDeleteDialog(null);
    }
  };

  return (
    <div className="flex flex-col flex-1 min-h-0">

      {/* API Error */}
      {apiError && (
        <div className="mb-4 p-4 bg-rose-50 border border-rose-200 rounded-xl flex items-center gap-3">
          <AlertCircle size={18} className="text-rose-600 flex-shrink-0" />
          <p className="text-sm text-rose-700 flex-1">{apiError}</p>
          <button onClick={onRefresh} className="text-sm text-rose-700 font-medium underline">Retry</button>
        </div>
      )}

      {/* Search */}
      <div className="flex items-center gap-3 mb-4">
        <div className="flex-1 flex items-center gap-2.5 px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl shadow-sm">
          <Search size={16} className="text-slate-400" />
          <input
            type="text"
            placeholder="Search roles by name or description..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
            className="flex-1 bg-transparent outline-none text-sm text-slate-700 placeholder-slate-400"
          />
        </div>
        <div className="px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-500 shadow-sm whitespace-nowrap">
          {filtered.length} role{filtered.length !== 1 ? "s" : ""}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
        <table className="w-full table-fixed">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="w-12 px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide"></th>
              <th className="w-[25%] px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Role Name</th>
              <th className="w-[35%] px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide hidden md:table-cell">Description</th>
              <th className="w-[10%] px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide">Users</th>
              <th className="w-[12%] px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide">Permissions</th>
              <th className="w-[16%] px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr>
                <td colSpan={6} className="py-16 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <Loader2 size={28} className="text-teal-500 animate-spin" />
                    <p className="text-slate-500 text-sm">Loading roles...</p>
                  </div>
                </td>
              </tr>
            ) : paginated.length === 0 ? (
              <tr>
                <td colSpan={6} className="py-16 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center">
                      <Shield size={28} className="text-slate-400" />
                    </div>
                    <p className="text-slate-500 font-medium">
                      {search ? "No roles match your search." : "No roles yet. Create your first role."}
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              paginated.map((role, idx) => (
                <tr key={role.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3.5 text-sm text-slate-400 font-mono">
                    {(currentPage - 1) * pageSize + idx + 1}
                  </td>
                  <td className="px-4 py-3.5">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                        {role.name?.substring(0, 2)?.toUpperCase()}
                      </div>
                      <p className="font-semibold text-slate-900 text-sm">{role.name}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3.5 hidden md:table-cell">
                    <p className="text-sm text-slate-500 line-clamp-2">
                      {role.description || <span className="text-slate-300 italic">No description</span>}
                    </p>
                  </td>
                  <td className="px-4 py-3.5 text-center">
                    <div className="flex items-center justify-center gap-1 text-sm text-slate-600">
                      <Users size={13} className="text-slate-400" /> {role.numberOfUsers ?? 0}
                    </div>
                  </td>
                  <td className="px-4 py-3.5 text-center">
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-teal-50 text-teal-700">
                      {Array.isArray(role.permissions)
                        ? role.permissions.filter((p) => p.hasPermission === true).length
                        : 0} active
                    </span>
                  </td>
                  <td className="px-4 py-3.5 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <ActionBtn icon={Eye}    label="View Permissions" color="text-slate-500 hover:text-violet-600 hover:bg-violet-50" onClick={() => onView(role)} />
                      {canEditRoles && (
                        <ActionBtn
                          icon={Edit2}
                          label="Edit"
                          color="text-slate-500 hover:text-teal-600 hover:bg-teal-50"
                          onClick={() => onEdit(role)}
                        />
                      )}
                      {canDeleteRoles && (
                        <ActionBtn
                          icon={Trash2}
                          label="Delete"
                          color="text-slate-500 hover:text-rose-600 hover:bg-rose-50"
                          onClick={() => setDeleteDialog(role)}
                        />
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {!loading && filtered.length > 0 && (
          <div className="border-t border-slate-200 px-4">
            <Pagination
              totalPages={totalPages}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              totalItems={filtered.length}
              pageSize={pageSize}
              onPageSizeChange={handlePageSizeChange}
              showPageSizeSelector={true}
            />
          </div>
        )}
      </div>

      {/* Delete Dialog */}
      {deleteDialog && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0">
                <Trash2 size={20} className="text-rose-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-slate-900 text-lg">Delete Role</h3>
                <p className="text-slate-500 text-sm mt-1">
                  Are you sure you want to delete <strong className="text-slate-800">"{deleteDialog.name}"</strong>? This cannot be undone.
                </p>
                {(deleteDialog.numberOfUsers ?? 0) > 0 && (
                  <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                    <AlertCircle size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-amber-700">
                      <strong>{deleteDialog.numberOfUsers} user{deleteDialog.numberOfUsers !== 1 ? "s are" : " is"} assigned</strong> to this role. Please reassign them first.
                    </p>
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setDeleteDialog(null)}
                className="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-xl text-sm font-medium hover:bg-slate-50">
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting || (deleteDialog.numberOfUsers ?? 0) > 0}
                className="px-4 py-2 bg-rose-600 text-white rounded-xl text-sm font-semibold hover:bg-rose-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {deleting && <Loader2 size={14} className="animate-spin" />}
                Delete Role
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── SCREEN 2: FORM + PERMISSION MATRIX ──────────────────────────────────────

function RoleFormPage({ role, roles, readOnly, onSave, onCancel }) {
  const isEdit = !!role;

  const [form, setForm] = useState({
    name:        role?.name        || "",
    description: role?.description || "",
    status:      role?.status      || "ACTIVE",
    copyFrom:    "",
  });
  const [errors, setErrors]             = useState({});
  const [dirty, setDirty]               = useState(false);
  const [saving, setSaving]             = useState(false);
  const [saveError, setSaveError]       = useState(null);
  const [permsLoading, setPermsLoading] = useState(false);
  const [permsError, setPermsError]     = useState(null);

  const [permMap, setPermMap]     = useState(() => buildPermMap(role?.permissions || []));
  const [expandedParents, setExpandedParents] = useState({});
  const [permSearch, setPermSearch]           = useState("");

  // Fetch all permissions + merge with role's existing ones
  useEffect(() => {
    const loadPermissions = async () => {
      setPermsLoading(true);
      setPermsError(null);
      try {
        const allPerms = await roleService.getAllPermissions();

        // Permissions that should never be shown in the Permission Matrix UI.
        // These are hardcoded to specific roles (Admin/Owner) and managed in the
        // backend only — admins shouldn't be able to grant them via the chip UI.
        const BACKEND_ONLY_PERMISSIONS = new Set([
          'back_office.bills.audit_log',
        ]);

        // Whitelist roles that ARE allowed to see backend-only permissions (still
        // hidden from the chip matrix, but kept in the map so the role's existing
        // grants don't get wiped on save).
        const roleName = (role?.name || '').toLowerCase();
        const isPrivilegedRole = roleName === 'admin' || roleName === 'owner';

        const filteredPerms = allPerms.filter((perm) =>
          !BACKEND_ONLY_PERMISSIONS.has(perm.code)
        );

        const merged = filteredPerms.map((perm) => {
          const existing = role?.permissions?.find((p) => p.code === perm.code);
          const isGranted = existing
            ? (existing.hasPermission ?? existing.active ?? false)
            : false;
          return {
            ...perm,
            active: isGranted,
            hasPermission: isGranted,
            // Preserve discountThreshold from saved role data (only set on the discount perm)
            ...(existing?.discountThreshold !== undefined && existing?.discountThreshold !== null
              && { discountThreshold: existing.discountThreshold }),
          };
        });
        const map = buildPermMap(merged);
        setPermMap(map);
        // Auto-expand parents with active perms when editing
        if (isEdit) {
          const parentSet = new Set();
          merged.forEach((p) => {
            if (p.active) parentSet.add(p.parentGroup || p.groupName || "General");
          });
          setExpandedParents(Object.fromEntries([...parentSet].map((k) => [k, true])));
        }
      } catch {
        setPermsError("Failed to load permissions. Please try again.");
      } finally {
        setPermsLoading(false);
      }
    };
    loadPermissions();
  }, [role]);

  function buildPermMap(permissions) {
    const map = {};
    permissions.forEach((p) => { map[p.code] = { ...p }; });
    return map;
  }

  // Build 2-level hierarchy: parentGroup → sub-groups → permissions
  const hierarchy = useMemo(() => {
    const perms = Object.values(permMap);
    const result = {};
    perms.forEach((perm) => {
      if (!perm.parentGroup) {
        // Root permission — parentGroup is null, groupName = parent name
        const parentName = perm.groupName || "General";
        if (!result[parentName]) result[parentName] = { rootPerms: [], subGroups: {} };
        result[parentName].rootPerms.push(perm);
      } else {
        // Sub-group permission
        const parentName = perm.parentGroup;
        if (!result[parentName]) result[parentName] = { rootPerms: [], subGroups: {} };
        const fullGroup = perm.groupName || "General";
        const subName = fullGroup.startsWith(parentName + " - ")
          ? fullGroup.slice(parentName.length + 3)
          : fullGroup;
        if (!result[parentName].subGroups[subName]) result[parentName].subGroups[subName] = [];
        result[parentName].subGroups[subName].push(perm);
      }
    });
    return result;
  }, [permMap]);

  const sortedParents = useMemo(() => {
    const keys = Object.keys(hierarchy);
    return PARENT_ORDER.filter((p) => keys.includes(p)).concat(keys.filter((p) => !PARENT_ORDER.includes(p)));
  }, [hierarchy]);

  // Filtered hierarchy for search
  const filteredHierarchy = useMemo(() => {
    if (!permSearch.trim()) return hierarchy;
    const q = permSearch.toLowerCase();
    const result = {};
    for (const [parent, data] of Object.entries(hierarchy)) {
      const filteredRoot = data.rootPerms.filter(
        (p) => p.name?.toLowerCase().includes(q) || p.code?.toLowerCase().includes(q),
      );
      const filteredSubs = {};
      for (const [sub, perms] of Object.entries(data.subGroups)) {
        const matched = perms.filter(
          (p) => p.name?.toLowerCase().includes(q) || p.code?.toLowerCase().includes(q),
        );
        if (matched.length > 0) filteredSubs[sub] = matched;
      }
      if (filteredRoot.length > 0 || Object.keys(filteredSubs).length > 0) {
        result[parent] = { rootPerms: filteredRoot, subGroups: filteredSubs };
      }
    }
    return result;
  }, [hierarchy, permSearch]);

  const filteredParents = useMemo(
    () => sortedParents.filter((p) => filteredHierarchy[p]),
    [sortedParents, filteredHierarchy],
  );

  // Helpers
  const getAllPermsInParent = (parentName) => {
    const data = hierarchy[parentName];
    if (!data) return [];
    const all = [...data.rootPerms];
    Object.values(data.subGroups).forEach((perms) => all.push(...perms));
    return all;
  };

  const parentStats = (parentName) => {
    const perms = getAllPermsInParent(parentName);
    return { total: perms.length, active: perms.filter((p) => permMap[p.code]?.active).length };
  };

  const subGroupStats = (perms) => ({
    total: perms.length,
    active: perms.filter((p) => permMap[p.code]?.active).length,
  });

  // Find the screen perm in a sub-group (the uniquely shortest code)
  const getScreenPerm = (perms) => {
    if (perms.length <= 1) return perms[0] || null;
    const sorted = [...perms].sort((a, b) => a.code.length - b.code.length);
    return sorted[0].code.length < sorted[1].code.length ? sorted[0] : null;
  };

  // Check if a screen perm is locked on (any action in its sub-group is active)
  const isScreenLocked = (screenPerm, allSubPerms) => {
    if (!screenPerm) return false;
    return allSubPerms.some((p) => p.code !== screenPerm.code && permMap[p.code]?.active);
  };

  // Check if a module root perm is locked on (any sub-group has active perms)
  const isModuleLocked = (parentName) => {
    const data = hierarchy[parentName];
    if (!data) return false;
    return Object.values(data.subGroups).some((perms) =>
      perms.some((p) => permMap[p.code]?.active)
    );
  };

  // Cascade: ensure screen perm + module root are ON when an action is toggled ON
  const cascadeUp = (u, code) => {
    // Find which parent & sub-group this code belongs to
    for (const [parentName, data] of Object.entries(hierarchy)) {
      for (const subPerms of Object.values(data.subGroups)) {
        if (subPerms.some((p) => p.code === code)) {
          // Turn on the screen perm in this sub-group
          const screen = getScreenPerm(subPerms);
          if (screen && !u[screen.code]?.active) {
            u[screen.code] = { ...u[screen.code], active: true };
          }
          // Turn on the module root perm
          for (const rp of data.rootPerms) {
            if (!u[rp.code]?.active) {
              u[rp.code] = { ...u[rp.code], active: true };
            }
          }
          return;
        }
      }
    }
  };

  // Toggle functions
  const togglePermission = (code) => {
    if (readOnly) return;
    setPermMap((prev) => {
      const u = { ...prev };
      const newVal = !u[code].active;
      u[code] = { ...u[code], active: newVal };
      if (newVal) cascadeUp(u, code);
      return u;
    });
    setDirty(true);
  };

  const toggleParentGroup = (parentName) => {
    if (readOnly) return;
    const perms = getAllPermsInParent(parentName);
    const allActive = perms.every((p) => permMap[p.code]?.active);
    setPermMap((prev) => {
      const u = { ...prev };
      perms.forEach((p) => { u[p.code] = { ...u[p.code], active: !allActive }; });
      return u;
    });
    setDirty(true);
  };

  const toggleSubGroup = (perms) => {
    if (readOnly) return;
    const allActive = perms.every((p) => permMap[p.code]?.active);
    const turning = !allActive; // true = turning ON, false = turning OFF
    setPermMap((prev) => {
      const u = { ...prev };
      perms.forEach((p) => { u[p.code] = { ...u[p.code], active: turning }; });
      // When turning on, cascade up to module root
      if (turning) {
        for (const [parentName, data] of Object.entries(hierarchy)) {
          const allSubPerms = Object.values(data.subGroups).flat();
          if (perms.some((p) => allSubPerms.includes(p))) {
            for (const rp of data.rootPerms) {
              if (!u[rp.code]?.active) u[rp.code] = { ...u[rp.code], active: true };
            }
            break;
          }
        }
      }
      return u;
    });
    setDirty(true);
  };

  const toggleAll = () => {
    if (readOnly) return;
    const all = Object.values(permMap);
    const allActive = all.every((p) => p.active);
    setPermMap((prev) => {
      const u = { ...prev };
      all.forEach((p) => { u[p.code] = { ...u[p.code], active: !allActive }; });
      return u;
    });
    setDirty(true);
  };

  const handleCopyFrom = (sourceRoleId) => {
    const sourceRole = roles.find((r) => r.id === sourceRoleId);
    if (!sourceRole?.permissions) return;
    setPermMap((prev) => {
      const u = { ...prev };
      sourceRole.permissions.forEach((p) => {
        if (u[p.code]) {
          const isGranted = p.hasPermission ?? p.active ?? false;
          u[p.code] = {
            ...u[p.code],
            active: isGranted,
            hasPermission: isGranted,
            // Copy discountThreshold along with the toggle state
            ...(p.discountThreshold !== undefined && p.discountThreshold !== null
              && { discountThreshold: p.discountThreshold }),
          };
        }
      });
      return u;
    });
    // Expand parent groups that received active permissions
    const parentSet = new Set();
    sourceRole.permissions.forEach((p) => {
      const isGranted = p.hasPermission ?? p.active ?? false;
      if (isGranted && permMap[p.code]) {
        parentSet.add(permMap[p.code].parentGroup || permMap[p.code].groupName || "General");
      }
    });
    setExpandedParents((prev) => ({
      ...prev,
      ...Object.fromEntries([...parentSet].map((k) => [k, true])),
    }));
    setDirty(true);
  };

  // Update the discount threshold cap for a permission (e.g., pos.dine_in.discount).
  // Accepts empty (clears the cap) or a number 0–100. Out-of-range values
  // are ignored so the field can't be saved in an invalid state.
  const updateDiscountThreshold = (code, raw) => {
    if (readOnly) return;
    if (raw !== "" && raw !== null && raw !== undefined) {
      const n = Number(raw);
      if (Number.isNaN(n) || n < 0 || n > 100) return;
    }
    setPermMap((prev) => ({
      ...prev,
      [code]: {
        ...prev[code],
        discountThreshold: raw === "" || raw === null ? null : Number(raw),
      },
    }));
    setDirty(true);
  };

  // Renders the "Max discount: [__] %" box inline next to the matching chip.
  // Returns null for any chip that isn't the discount permission or isn't active.
  const renderMaxDiscountInput = (perm, isActive) => {
    if (perm.code !== MAX_DISCOUNT_PERM_CODE || !isActive) return null;
    const value = permMap[perm.code]?.discountThreshold;
    return (
      <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-md bg-white border border-teal-300">
        <span className="text-xs text-slate-700 font-medium">Max discount</span>
        <div className="inline-flex items-center bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5">
          <input
            type="number"
            min="0"
            max="100"
            step="1"
            placeholder="—"
            value={value ?? ""}
            onChange={(e) => updateDiscountThreshold(perm.code, e.target.value)}
            disabled={readOnly}
            className="w-10 text-center bg-transparent outline-none text-teal-700 font-bold text-xs disabled:text-slate-400 disabled:cursor-not-allowed [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          />
          <span className="text-xs text-slate-500 font-medium ml-0.5">%</span>
        </div>
      </div>
    );
  };

  const updateForm = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
    setDirty(true);
    if (key === "copyFrom" && value) handleCopyFrom(value);
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim())                 e.name = "Role name is required";
    else if (form.name.trim().length < 3)  e.name = "Minimum 3 characters";
    else if (form.name.trim().length > 50) e.name = "Maximum 50 characters";
    else {
      const conflict = roles.find(
        (r) => r.name.toLowerCase() === form.name.trim().toLowerCase() && r.id !== role?.id
      );
      if (conflict) e.name = "A role with this name already exists";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const isValid = form.name.trim().length >= 3;

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    setSaveError(null);
    try {
      // FIX 1: Only send permissions that are active (toggled ON) to the API
      // (discountThreshold rides along automatically via the {...p} spread)
      const permissions = Object.values(permMap)
        .filter((p) => p.active)
        .map((p) => ({
          ...p,
          hasPermission: true,
        }));
      await onSave(
        { name: form.name.trim(), description: form.description.trim(), status: form.status },
        permissions,
      );
    } catch {
      setSaveError("Failed to save role. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (dirty && !window.confirm("You have unsaved changes. Leave anyway?")) return;
    onCancel();
  };

  const otherRoles  = roles.filter((r) => r.id !== role?.id);
  const totalActive = Object.values(permMap).filter((p) => p.active).length;
  const totalPerms  = Object.values(permMap).length;

  return (
    <div className="flex flex-col flex-1 min-h-0 bg-gradient-to-br from-slate-50 via-white to-slate-50 overflow-x-hidden">

      {/* Sticky Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-slate-200/60 px-4 py-4 sticky top-0 z-20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={handleCancel}
              className="p-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 transition-all">
              <ArrowLeft size={18} />
            </button>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl font-bold text-slate-900">
                  {readOnly ? "View Role" : isEdit ? "Edit Role" : "New Role"}
                </h1>
                {isEdit && (
                  <span className="px-3 py-1 bg-teal-100 text-teal-700 text-sm font-medium rounded-full">
                    {role.name}
                  </span>
                )}
                {readOnly && (
                  <span className="px-2.5 py-0.5 bg-violet-100 text-violet-700 text-xs font-semibold rounded-full">
                    Read-Only
                  </span>
                )}
              </div>
              <p className="text-sm text-slate-500 mt-0.5">
                {readOnly
                  ? "Viewing role details and permission matrix"
                  : isEdit
                  ? `Updating role and permissions for ${role.name}`
                  : "Add a new role and assign module permissions"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={handleCancel}
              className="px-5 py-2.5 bg-white border border-slate-200 rounded-xl text-slate-700 hover:bg-slate-50 text-sm font-medium">
              {readOnly ? "Close" : "Cancel"}
            </button>
            {!readOnly && (
              <button onClick={handleSave} disabled={saving || !isValid || permsLoading}
                className="px-5 py-2.5 bg-teal-600 text-white rounded-xl hover:bg-teal-700 flex items-center gap-2 text-sm font-semibold disabled:opacity-50 shadow-sm shadow-teal-200">
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                {isEdit ? "Update Role" : "Create Role"}
              </button>
            )}
          </div>
        </div>
        {saveError && (
          <div className="mt-3 p-3 bg-rose-50 border border-rose-200 rounded-xl flex items-center gap-2">
            <AlertCircle size={16} className="text-rose-600 flex-shrink-0" />
            <p className="text-sm text-rose-700">{saveError}</p>
          </div>
        )}
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        <div className="px-4 py-4 space-y-6">

          {/* ── CARD 1: Role Information ── */}
          <FormCard icon={Shield} title="Role Information" subtitle="Basic details about this role">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">

              <FormInput
                icon={ShieldCheck}
                label="Role Name"
                required
                error={errors.name}
                disabled={readOnly}
                value={form.name}
                onChange={(e) => updateForm("name", e.target.value)}
                placeholder="e.g., Floor Manager"
                maxLength={50}
                hint={`${form.name.length}/50`}
              />

              {/* Status Toggle */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Status <span className="text-rose-500">*</span>
                </label>
                <div className="flex items-center h-[42px]">
                  <button
                    type="button"
                    disabled={readOnly}
                    onClick={() => !readOnly && updateForm("status", form.status === "ACTIVE" ? "INACTIVE" : "ACTIVE")}
                    className="flex items-center gap-3"
                  >
                    <div className={`relative w-12 h-6 rounded-full transition-colors duration-200 ${
                      form.status === "ACTIVE" ? "bg-teal-600" : "bg-slate-200"
                    } ${readOnly ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}>
                      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${
                        form.status === "ACTIVE" ? "translate-x-6" : ""
                      }`} />
                    </div>
                    <span className={`text-sm font-medium ${form.status === "ACTIVE" ? "text-teal-700" : "text-slate-500"}`}>
                      {form.status === "ACTIVE" ? "Active" : "Inactive"}
                    </span>
                  </button>
                </div>
              </div>

              <FormSelect
                icon={Copy}
                label="Copy Permissions From"
                disabled={readOnly || otherRoles.length === 0}
                value={form.copyFrom}
                onChange={(e) => updateForm("copyFrom", e.target.value)}
                hint={!readOnly && otherRoles.length > 0 ? "Copies permission toggles from selected role" : undefined}
                options={[
                  { label: otherRoles.length === 0 ? "No other roles yet" : "None — start fresh", value: "" },
                  ...otherRoles.map((r) => ({ label: r.name, value: r.id })),
                ]}
              />
            </div>

            <div className="mt-5 pt-5 border-t border-slate-100">
              <FormTextarea
                icon={FileText}
                label="Description"
                disabled={readOnly}
                value={form.description}
                onChange={(e) => updateForm("description", e.target.value)}
                placeholder="Brief description of what this role can do..."
                maxLength={200}
                rows={2}
                hint={`${form.description.length}/200`}
              />
            </div>
          </FormCard>

          {/* ── CARD 2: Permission Matrix ── */}
          <FormCard
            icon={ShieldCheck}
            title="Permission Matrix"
            subtitle={
              permsLoading
                ? "Loading permissions from server..."
                : readOnly
                ? `Permissions assigned to this role — ${totalActive} of ${totalPerms} active (read-only)`
                : `Toggle individual permissions — ${totalActive} of ${totalPerms} active`
            }
          >
            {/* Loading state */}
            {permsLoading ? (
              <div className="py-12 text-center flex flex-col items-center gap-3">
                <Loader2 size={28} className="text-teal-500 animate-spin" />
                <p className="text-slate-500 text-sm">Loading permissions...</p>
              </div>

            /* Error state */
            ) : permsError ? (
              <div className="py-8 text-center">
                <div className="inline-flex items-center gap-2 px-4 py-3 bg-rose-50 border border-rose-200 rounded-xl">
                  <AlertCircle size={16} className="text-rose-600" />
                  <p className="text-sm text-rose-700">{permsError}</p>
                </div>
              </div>

            /* Empty state */
            ) : totalPerms === 0 ? (
              <div className="py-12 text-center">
                <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <ShieldCheck size={28} className="text-slate-400" />
                </div>
                <p className="text-slate-500 text-sm">No permissions available.</p>
                <p className="text-slate-400 text-xs mt-1">The permissions API returned no results.</p>
              </div>

            ) : (
              <>
                {/* Search + Stats + Toggle All */}
                <div className="flex flex-wrap items-center gap-3 mb-5">
                  <div className="flex-1 min-w-[200px] flex items-center gap-2.5 px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl">
                    <Search size={15} className="text-slate-400 flex-shrink-0" />
                    <input
                      type="text"
                      placeholder="Search permissions..."
                      value={permSearch}
                      onChange={(e) => setPermSearch(e.target.value)}
                      className="flex-1 bg-transparent outline-none text-sm text-slate-700 placeholder-slate-400"
                    />
                    {permSearch && (
                      <button onClick={() => setPermSearch("")} className="p-0.5 hover:bg-slate-200 rounded">
                        <X size={14} className="text-slate-400" />
                      </button>
                    )}
                  </div>
                  <span className="text-sm text-slate-500 font-medium whitespace-nowrap">
                    {totalActive} of {totalPerms} selected
                  </span>
                  {!readOnly && (
                    <button onClick={toggleAll}
                      className="text-xs px-3.5 py-2 bg-slate-100 hover:bg-teal-50 hover:text-teal-700 text-slate-600 rounded-lg font-medium transition-colors whitespace-nowrap">
                      {Object.values(permMap).every((p) => p.active) ? "Deselect All" : "Select All"}
                    </button>
                  )}
                </div>

                {/* Parent Group Cards */}
                <div className="space-y-3">
                  {(permSearch.trim() ? filteredParents : sortedParents).map((parentName) => {
                    const meta = MODULE_META[parentName] || {
                      icon: ShieldCheck,
                      iconBg: "bg-gradient-to-br from-slate-500 to-slate-600",
                      headerBg: "bg-slate-50",
                      badge: "bg-slate-100 text-slate-700",
                      border: "border-slate-200",
                    };
                    const ModIcon = meta.icon;
                    const data = (permSearch.trim() ? filteredHierarchy : hierarchy)[parentName];
                    if (!data) return null;
                    const stats = parentStats(parentName);
                    const isParentOpen = !!permSearch.trim() || !!expandedParents[parentName];
                    const allParentPerms = getAllPermsInParent(parentName);
                    const allOn = allParentPerms.length > 0 && allParentPerms.every((p) => permMap[p.code]?.active);
                    const someOn = allParentPerms.some((p) => permMap[p.code]?.active) && !allOn;

                    return (
                      <div key={parentName} className={`border ${meta.border} rounded-xl overflow-hidden`}>
                        {/* Parent Header */}
                        <div className={`flex items-center gap-3 px-4 py-3 ${meta.headerBg} border-b ${meta.border}`}>
                          <button
                            onClick={() => setExpandedParents((prev) => ({ ...prev, [parentName]: !prev[parentName] }))}
                            className="p-0.5 hover:bg-white/60 rounded flex-shrink-0"
                          >
                            {isParentOpen
                              ? <ChevronDown size={16} className="text-slate-500" />
                              : <ChevronRight size={16} className="text-slate-500" />}
                          </button>
                          <PermCheckbox
                            checked={allOn}
                            indeterminate={someOn}
                            disabled={readOnly}
                            onChange={() => toggleParentGroup(parentName)}
                          />
                          <div className={`w-7 h-7 rounded-lg ${meta.iconBg} flex items-center justify-center flex-shrink-0`}>
                            <ModIcon size={14} className="text-white" />
                          </div>
                          <button
                            onClick={() => readOnly
                              ? setExpandedParents((prev) => ({ ...prev, [parentName]: !prev[parentName] }))
                              : toggleParentGroup(parentName)
                            }
                            className={`font-semibold text-slate-800 text-sm flex-1 text-left cursor-pointer ${!readOnly ? "hover:text-teal-700" : ""}`}
                          >
                            {parentName}
                          </button>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${meta.badge}`}>
                            {stats.active}/{stats.total}
                          </span>
                        </div>

                        {/* Parent Body — Chip Layout */}
                        {isParentOpen && (
                          <div className="px-4 py-3 space-y-3">
                            {/* Root permissions as chips */}
                            {data.rootPerms.length > 0 && (() => {
                              const moduleLocked = isModuleLocked(parentName);
                              return (
                                <div className={`flex flex-wrap gap-1.5 px-3 py-2.5 rounded-lg ${meta.headerBg}`}>
                                  {data.rootPerms.map((perm) => {
                                    const isActive = !!permMap[perm.code]?.active;
                                    const locked = !readOnly && isActive && moduleLocked;
                                    return (
                                      <React.Fragment key={perm.code}>
                                        <button
                                          type="button"
                                          title={locked ? `${perm.code} (auto-selected — active sub-permissions depend on this)` : perm.code}
                                          onClick={() => !readOnly && !locked && togglePermission(perm.code)}
                                          disabled={readOnly || locked}
                                          className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border transition-all ${
                                            locked
                                              ? "bg-teal-100 border-teal-300 text-teal-800 font-medium cursor-not-allowed opacity-75"
                                              : readOnly
                                              ? "cursor-default"
                                              : "cursor-pointer"
                                          } ${!locked && (
                                            isActive
                                              ? "bg-teal-50 border-teal-200 text-teal-700 font-medium"
                                              : "bg-white border-slate-200 text-slate-500"
                                          )} ${!readOnly && !locked && !isActive ? "hover:border-teal-400 hover:bg-teal-50" : ""}`}
                                        >
                                          {isActive && (
                                            <svg width="8" height="8" viewBox="0 0 10 10" fill="none" className="flex-shrink-0">
                                              <path d="M2 5L4.5 7.5L8 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                          )}
                                          {perm.name}
                                          {locked && (
                                            <svg width="8" height="8" viewBox="0 0 24 24" fill="none" className="flex-shrink-0 ml-0.5">
                                              <rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" strokeWidth="3" />
                                              <path d="M7 11V7a5 5 0 0110 0v4" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                                            </svg>
                                          )}
                                        </button>
                                        {renderMaxDiscountInput(perm, isActive)}
                                      </React.Fragment>
                                    );
                                  })}
                                </div>
                              );
                            })()}

                            {/* Sub-group sections */}
                            {Object.entries(data.subGroups).map(([subName, perms]) => {
                              const sStats = subGroupStats(perms);
                              const subAllOn = perms.every((p) => permMap[p.code]?.active);
                              const subSomeOn = perms.some((p) => permMap[p.code]?.active) && !subAllOn;
                              const screenPerm = getScreenPerm(perms);
                              const actionPerms = screenPerm ? perms.filter((p) => p.code !== screenPerm.code) : perms;
                              const screenLocked = isScreenLocked(screenPerm, perms);

                              return (
                                <div key={`${parentName}::${subName}`}>
                                  {/* Sub-group label row */}
                                  <div className="flex items-center gap-2 py-1.5 px-1">
                                    <PermCheckbox
                                      checked={subAllOn}
                                      indeterminate={subSomeOn}
                                      disabled={readOnly}
                                      onChange={() => toggleSubGroup(perms)}
                                    />
                                    <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">{subName}</span>
                                    <div className="flex-1 h-px bg-slate-100" />
                                    <span className="text-xs text-slate-400 font-mono">{sStats.active}/{sStats.total}</span>
                                  </div>
                                  {/* Permission chips */}
                                  <div className="flex flex-wrap gap-1.5 pl-7">
                                    {/* Screen perm chip (locked when actions are active) */}
                                    {screenPerm && (() => {
                                      const isActive = !!permMap[screenPerm.code]?.active;
                                      const locked = !readOnly && isActive && screenLocked;
                                      return (
                                        <React.Fragment key={screenPerm.code}>
                                          <button
                                            type="button"
                                            title={locked ? `${screenPerm.code} (auto-selected — active actions depend on this)` : screenPerm.code}
                                            onClick={() => !readOnly && !locked && togglePermission(screenPerm.code)}
                                            disabled={readOnly || locked}
                                            className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border transition-all ${
                                              locked
                                                ? "bg-teal-100 border-teal-300 text-teal-800 font-medium cursor-not-allowed opacity-75"
                                                : readOnly
                                                ? "cursor-default"
                                                : "cursor-pointer"
                                            } ${!locked && (
                                              isActive
                                                ? "bg-teal-50 border-teal-200 text-teal-700 font-medium"
                                                : "bg-white border-slate-200 text-slate-500"
                                            )} ${!readOnly && !locked && !isActive ? "hover:border-teal-400 hover:bg-teal-50" : ""}`}
                                          >
                                            {isActive && (
                                              <svg width="8" height="8" viewBox="0 0 10 10" fill="none" className="flex-shrink-0">
                                                <path d="M2 5L4.5 7.5L8 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                              </svg>
                                            )}
                                            {screenPerm.name}
                                            {locked && (
                                              <svg width="8" height="8" viewBox="0 0 24 24" fill="none" className="flex-shrink-0 ml-0.5">
                                                <rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" strokeWidth="3" />
                                                <path d="M7 11V7a5 5 0 0110 0v4" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                                              </svg>
                                            )}
                                          </button>
                                          {renderMaxDiscountInput(screenPerm, isActive)}
                                        </React.Fragment>
                                      );
                                    })()}
                                    {/* Action perm chips */}
                                    {actionPerms.map((perm) => {
                                      const isActive = !!permMap[perm.code]?.active;
                                      return (
                                        <React.Fragment key={perm.code}>
                                          <button
                                            type="button"
                                            title={perm.code}
                                            onClick={() => !readOnly && togglePermission(perm.code)}
                                            disabled={readOnly}
                                            className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border transition-all ${
                                              readOnly ? "cursor-default" : "cursor-pointer"
                                            } ${
                                              isActive
                                                ? "bg-teal-50 border-teal-200 text-teal-700 font-medium"
                                                : "bg-white border-slate-200 text-slate-500"
                                            } ${!readOnly && !isActive ? "hover:border-teal-400 hover:bg-teal-50" : ""}`}
                                          >
                                            {isActive && (
                                              <svg width="8" height="8" viewBox="0 0 10 10" fill="none" className="flex-shrink-0">
                                                <path d="M2 5L4.5 7.5L8 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                              </svg>
                                            )}
                                            {perm.name}
                                          </button>
                                          {renderMaxDiscountInput(perm, isActive)}
                                        </React.Fragment>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* No search results */}
                  {permSearch.trim() && filteredParents.length === 0 && (
                    <div className="py-8 text-center">
                      <p className="text-slate-500 text-sm">No permissions match &ldquo;{permSearch}&rdquo;</p>
                    </div>
                  )}
                </div>
              </>
            )}
          </FormCard>

        </div>
      </div>
    </div>
  );
}

// ─── FORM ATOMS ───────────────────────────────────────────────────────────────

function FormInput({ icon: Icon, label, error, required, disabled = false, extraClass = "", hint, ...props }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1.5">
        {label} {required && <span className="text-rose-500">*</span>}
      </label>
      <div className="relative">
        {Icon && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
            <Icon size={16} />
          </div>
        )}
        <input
          {...props}
          disabled={disabled}
          className={`w-full ${Icon ? "pl-10" : "pl-4"} pr-4 py-2.5 border rounded-xl text-sm outline-none transition-all ${
            error
              ? "border-rose-300 bg-rose-50 focus:border-rose-500 focus:ring-2 focus:ring-rose-500/20"
              : "border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
          } ${disabled ? "bg-slate-100 text-slate-500 cursor-not-allowed" : "bg-white"} ${extraClass}`}
        />
      </div>
      {error  && <p className="text-xs text-rose-500 mt-1.5 flex items-center gap-1"><AlertCircle size={12} />{error}</p>}
      {!error && hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}

function FormSelect({ icon: Icon, label, error, required, disabled = false, options = [], hint, ...props }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1.5">
        {label} {required && <span className="text-rose-500">*</span>}
      </label>
      <div className="relative">
        {Icon && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
            <Icon size={16} />
          </div>
        )}
        <select
          {...props}
          disabled={disabled}
          className={`w-full ${Icon ? "pl-10" : "pl-4"} pr-8 py-2.5 border rounded-xl text-sm outline-none appearance-none transition-all ${
            error
              ? "border-rose-300 bg-rose-50 focus:border-rose-500 focus:ring-2 focus:ring-rose-500/20"
              : "border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
          } ${disabled ? "bg-slate-100 text-slate-500 cursor-not-allowed" : "bg-white"}`}
        >
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </div>
      {error  && <p className="text-xs text-rose-500 mt-1.5 flex items-center gap-1"><AlertCircle size={12} />{error}</p>}
      {!error && hint && <p className="text-xs text-slate-400 mt-1 flex items-center gap-1"><Info size={11} />{hint}</p>}
    </div>
  );
}

function FormTextarea({ icon: Icon, label, error, required, disabled = false, hint, rows = 3, ...props }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1.5">
        {label} {required && <span className="text-rose-500">*</span>}
      </label>
      <div className="relative">
        {Icon && (
          <div className="absolute left-3 top-3 text-slate-400 pointer-events-none">
            <Icon size={16} />
          </div>
        )}
        <textarea
          {...props}
          rows={rows}
          disabled={disabled}
          className={`w-full ${Icon ? "pl-10" : "pl-4"} pr-4 py-2.5 border rounded-xl text-sm outline-none resize-none transition-all ${
            error
              ? "border-rose-300 bg-rose-50 focus:border-rose-500 focus:ring-2 focus:ring-rose-500/20"
              : "border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
          } ${disabled ? "bg-slate-100 text-slate-500 cursor-not-allowed" : "bg-white"}`}
        />
      </div>
      {error  && <p className="text-xs text-rose-500 mt-1.5 flex items-center gap-1"><AlertCircle size={12} />{error}</p>}
      {!error && hint && <p className="text-xs text-slate-400 mt-1 text-right">{hint}</p>}
    </div>
  );
}

// ─── SHARED ATOMS ─────────────────────────────────────────────────────────────

function FormCard({ icon: Icon, title, subtitle, children }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200/60 overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-teal-600 flex items-center justify-center flex-shrink-0">
            <Icon size={20} className="text-white" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-slate-800">{title}</h3>
            {subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}
          </div>
        </div>
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

function ActionBtn({ icon: Icon, label, color, onClick }) {
  return (
    <button title={label} onClick={onClick} className={`p-1.5 rounded-lg transition-colors ${color}`}>
      <Icon size={15} />
    </button>
  );
}

function PermCheckbox({ checked, indeterminate, disabled, onChange }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onChange(); }}
      disabled={disabled}
      className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all flex-shrink-0 ${
        disabled ? "cursor-not-allowed" : "cursor-pointer hover:scale-110"
      } ${
        checked
          ? "bg-teal-600 border-teal-600"
          : indeterminate
          ? "bg-teal-100 border-teal-400"
          : "bg-white border-slate-300 hover:border-teal-400"
      }`}
    >
      {checked && (
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
          <path d="M2 5L4.5 7.5L8 3" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
      {indeterminate && !checked && <div className="w-2 h-0.5 bg-teal-600 rounded" />}
    </button>
  );
}